import React, { useState, useEffect } from "react";
import { Snackbar, Alert } from "@mui/material";
import { usePatterns } from "../contexts/PatternContext";
import { createEmptyChart } from "../services/patternCompiler";
import { ChartMakerLayout, StitchGuideModal } from "../components/chartmaker";
import SEO from "../components/common/SEO";
import "../components/chartmaker/chartmaker.css";

const ChartMaker = () => {
  const { createPattern } = usePatterns();

  const [gridSize, setGridSize] = useState({ height: 10, width: 10 });
  const [chart, setChart] = useState(createEmptyChart(10, 10));
  const [patternTitle, setPatternTitle] = useState("");
  const [selectedSymbol, setSelectedSymbol] = useState("◊"); // Default to knit stitch
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [isGeneratingChart, setIsGeneratingChart] = useState(false);
  const [selectedStitchClickHandler, setSelectedStitchClickHandler] =
    useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const handleCreateGrid = async (newSize = gridSize) => {
    const totalCells = newSize.height * newSize.width;

    // Show loading state for large charts
    if (totalCells > 1000) {
      setIsGeneratingChart(true);

      // Use setTimeout to allow UI to update before heavy computation
      setTimeout(() => {
        const newChart = createEmptyChart(newSize.height, newSize.width);
        setChart(newChart);
        setIsGeneratingChart(false);
      }, 50);
    } else {
      const newChart = createEmptyChart(newSize.height, newSize.width);
      setChart(newChart);
    }
  };

  const handleSavePattern = () => {
    if (!patternTitle.trim()) {
      setSnackbar({ open: true, message: "Please enter a pattern title", severity: "warning" });
      return;
    }

    const patternData = {
      title: patternTitle,
      chart: chart,
    };

    createPattern(patternData);
    setSnackbar({ open: true, message: "Pattern saved successfully!", severity: "success" });
  };

  const handleExport = () => {
    setExportModalOpen(true);
  };

  const handleExportModalClose = () => {
    setExportModalOpen(false);
  };

  const handleImportPattern = (patternData) => {
    setChart(patternData.chart);
    setPatternTitle(patternData.title);
    setSnackbar({ open: true, message: "Pattern imported successfully!", severity: "success" });
  };

  const handleClearGrid = (newChart) => {
    setChart(newChart);
  };

  const handlePatternTitleChange = (e) => {
    setPatternTitle(e.target.value);
  };

  const handleInfoModalOpen = () => {
    setInfoModalOpen(true);
  };

  const handleInfoModalClose = () => {
    setInfoModalOpen(false);
  };

  // Warn user before leaving if they have unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Check if there are any non-default values in the chart
      const hasChanges = Object.values(chart.rows).some((row) =>
        row.some((cell) => cell !== "-"),
      );

      if (hasChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [chart]);

  return (
    <>
      <SEO
        title="Chart Maker"
        description="Design custom knitting patterns with our interactive chart maker. Create stitch-by-stitch charts up to 50x50, use our stitch symbol palette, and export professional PDFs or JSON files."
        keywords="knitting chart maker, stitch chart designer, pattern creator, knitting grid, chart designer, knit pattern maker, stitch symbol chart, knitting design tool"
      />
      <div className="chartmaker-container">
      <ChartMakerLayout
        gridSize={gridSize}
        chart={chart}
        selectedSymbol={selectedSymbol}
        onSizeChange={setGridSize}
        onCreateGrid={handleCreateGrid}
        onChartChange={setChart}
        onSymbolSelect={setSelectedSymbol}
        onPatternTitleChange={handlePatternTitleChange}
        onSavePattern={handleSavePattern}
        onClearGrid={handleClearGrid}
        onExport={handleExport}
        onInfoModalOpen={handleInfoModalOpen}
        exportModalOpen={exportModalOpen}
        onExportModalClose={handleExportModalClose}
        onImportPattern={handleImportPattern}
        patternTitle={patternTitle}
        isGeneratingChart={isGeneratingChart}
        selectedStitchClickHandler={selectedStitchClickHandler}
        setSelectedStitchClickHandler={setSelectedStitchClickHandler}
      />

      <StitchGuideModal open={infoModalOpen} onClose={handleInfoModalClose} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
    </>
  );
};

export default ChartMaker;
